import { isNil } from 'lodash-es';
import { useCallback } from 'react';
import { Control, FieldValues, useController } from 'react-hook-form';

import {
  AddOnAssignmentSchema,
  buildAddOnAssignmentDedupKey,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

import type { SiteRateScheduleDTO } from './SiteRatesForm';

// Whether a list carries a real add-on: a non-deleted assignment pointing at a
// catalog `id`/`addOnId`, rather than an empty placeholder row.
export const hasRealAddOn = (
  addOns: AddOnAssignmentSchema[] | null | undefined,
): boolean =>
  Boolean(
    addOns?.some(
      (addOn) => !addOn.isDeleted && Boolean(addOn.addOn?.id || addOn.addOn?.addOnId),
    ),
  );

// Turning "Match vendor rate" on runs submit-time `deriveDriverFromVendor`, which
// deletes the existing driver add-ons and mirrors the vendor ones — so a card that
// already has driver add-ons must not default to matching.
const hasDriverAddOn = (rate: SiteRateScheduleDTO): boolean =>
  hasRealAddOn(rate.driverAddOns);

// Whether the rate has an explicit driver setup the auto-default must not
// clobber — an explicit driver rate (value or type) or a driver add-on.
// Used by `resolveUseVendorRateForDriver` to avoid silently flipping the
// "use vendor rate" default for rate cards that already carry driver data.
const hasExplicitDriverSetup = (rate: SiteRateScheduleDTO): boolean =>
  Boolean(
    rate.driverRate && (!isNil(rate.driverRate.rate) || rate.driverRate.type?.value),
  ) || hasDriverAddOn(rate);

// A card's matching state is form-only — the API never persists
// `useVendorRateForDriver` — so a SAVED card's state is re-derived from the
// values themselves. "Match vendor rate" copies the vendor rate AND add-ons
// onto the driver on save, so a card is matching when both sides carry the
// same rate value/type and the same effective add-on set — including when both
// sets are empty.
const normalizeRateValue = (value: number | string | null | undefined) =>
  isNil(value) || value === '' ? null : Number(value);

// Identity keyed the same way the add-on uniqueness constraint is, so a
// mirrored driver row matches its vendor source despite the two carrying
// different assignment ids.
const addOnIdentities = (addOns: AddOnAssignmentSchema[] | null | undefined) =>
  new Set(
    (addOns ?? [])
      .filter((addOn) => !addOn.isDeleted)
      .map(buildAddOnAssignmentDedupKey)
      .filter((key): key is string => key !== null),
  );

const driverRateMirrorsVendorRate = (rate: SiteRateScheduleDTO): boolean => {
  if (
    normalizeRateValue(rate.driverRate?.rate) !==
      normalizeRateValue(rate.vendorRate?.rate) ||
    (rate.driverRate?.type?.value ?? null) !== (rate.vendorRate?.type?.value ?? null)
  ) {
    return false;
  }

  const driverAddOns = addOnIdentities(rate.driverAddOns);
  const vendorAddOns = addOnIdentities(rate.vendorAddOns);
  return (
    driverAddOns.size === vendorAddOns.size &&
    [...driverAddOns].every((key) => vendorAddOns.has(key))
  );
};

// Single source of truth for the "use vendor rate for driver" default —
// used to seed form state in both the project rate card flow
// (FreightRatesManager) and the legacy site rate flow (SiteRatesFormInner).
//
// The FF governs only a rate card that is being CREATED — one with no server
// id; a saved card reads its state off its own values instead. The id check is
// falsy rather than `=== undefined` so an empty-string id also reads as
// unsaved.
//
// Copy & Edit / Duplicate Order and the in-form card Duplicate all produce
// cards with no id, so they keep the default. A caller re-resolving a card that
// came out of form state must clear `useVendorRateForDriver` along with the id:
// form state always holds a concrete boolean, so the `??` below would otherwise
// return it before the id and feature-flag checks ever run.
//
// TRE-15083: `hasExplicitDriverSetup` also suppresses the default when the card
// carries a driver add-on but a null legacy driver rate — defaulting such a
// migrated card to matching would mirror the vendor rate/add-ons over that
// driver add-on on save.
export const resolveUseVendorRateForDriver = (
  rate: SiteRateScheduleDTO,
  defaultVendorRateToDriverFeatureFlagEnabled: boolean,
): boolean =>
  rate.useVendorRateForDriver ??
  (rate.id
    ? driverRateMirrorsVendorRate(rate)
    : defaultVendorRateToDriverFeatureFlagEnabled && !hasExplicitDriverSetup(rate));

interface UseVendorRateForDriverToggleArgs {
  control: Control<FieldValues>;
  index: number;
}

// Shared toggle handler for the per-rate-card "Use vendor rate and add-ons
// for driver rate" control. Flips the boolean only; driver rate/add-on form
// state is left alone so toggling OFF restores whatever the user had typed.
// Submit-time `deriveDriverFromVendor` mirrors the vendor values onto the
// persisted record when the toggle is true.
export const useVendorRateForDriverToggle = ({
  control,
  index,
}: UseVendorRateForDriverToggleArgs) => {
  const { field: useVendorRateForDriverField } = useController({
    control,
    name: `siteRates.${index}.useVendorRateForDriver`,
  });
  const useVendorRateForDriver = Boolean(useVendorRateForDriverField.value);

  const handleToggleVendorRateForDriver = useCallback(
    (checked: boolean) => {
      useVendorRateForDriverField.onChange(checked);
    },
    [useVendorRateForDriverField],
  );

  return { useVendorRateForDriver, handleToggleVendorRateForDriver };
};
